/** Defaults for new employer job postings (mirrors Opportunity fields). */

export type EmployerPostingPreferences = {
  kind: 'employer_posting_defaults'
  default_employment_type: string
  default_work_mode: string
  default_city: string
  default_country_code: string
  default_salary_visibility: 'shown' | 'not_disclosed'
  notify_on_new_applications: boolean
}

export function emptyEmployerPostingPreferences(
  defaultCountryCode = '',
): EmployerPostingPreferences {
  return {
    kind: 'employer_posting_defaults',
    default_employment_type: '',
    default_work_mode: '',
    default_city: '',
    default_country_code: defaultCountryCode,
    default_salary_visibility: 'not_disclosed',
    notify_on_new_applications: true,
  }
}

export function parseEmployerPostingPreferences(
  raw: unknown,
  defaultCountryCode = '',
): EmployerPostingPreferences {
  const base = emptyEmployerPostingPreferences(defaultCountryCode)
  if (!raw || typeof raw !== 'object') return base

  const data = raw as Record<string, unknown>
  if (data.kind !== 'employer_posting_defaults') return base

  return {
    kind: 'employer_posting_defaults',
    default_employment_type:
      typeof data.default_employment_type === 'string' ? data.default_employment_type : '',
    default_work_mode: typeof data.default_work_mode === 'string' ? data.default_work_mode : '',
    default_city: typeof data.default_city === 'string' ? data.default_city : '',
    default_country_code:
      typeof data.default_country_code === 'string'
        ? data.default_country_code
        : base.default_country_code,
    default_salary_visibility:
      data.default_salary_visibility === 'shown' ? 'shown' : 'not_disclosed',
    notify_on_new_applications:
      typeof data.notify_on_new_applications === 'boolean'
        ? data.notify_on_new_applications
        : base.notify_on_new_applications,
  }
}

/** Fields on a new job draft that org posting defaults can pre-fill. */
export type EmployerPostingPreferencesJobDraft = {
  employment_type: string
  work_mode: string
  city: string
  country_code: string
  salary_visibility: 'shown' | 'not_disclosed'
}

export function emptyEmployerPostingPreferencesJobDraft(
  fallbackCountryCode = '',
): EmployerPostingPreferencesJobDraft {
  return {
    employment_type: '',
    work_mode: '',
    city: '',
    country_code: fallbackCountryCode,
    salary_visibility: 'not_disclosed',
  }
}

/** Map org posting defaults into fields for a new job create form. */
export function employerPostingPreferencesToJobDraft(
  prefs: EmployerPostingPreferences,
  fallbackCountryCode = '',
): EmployerPostingPreferencesJobDraft {
  const draft = emptyEmployerPostingPreferencesJobDraft(fallbackCountryCode)

  if (prefs.default_employment_type.trim()) {
    draft.employment_type = prefs.default_employment_type.trim()
  }

  if (prefs.default_work_mode.trim()) {
    draft.work_mode = prefs.default_work_mode.trim()
  }

  const country = prefs.default_country_code.trim()
  draft.country_code = country || fallbackCountryCode

  draft.salary_visibility = prefs.default_salary_visibility

  const workMode = draft.work_mode
  if ((workMode === 'onsite' || workMode === 'hybrid') && prefs.default_city.trim()) {
    draft.city = prefs.default_city.trim()
  }

  return draft
}

/** Org API payload — posting fields only (email toggle uses user notifications). */
export function employerPostingPreferencesForOrgApi(
  prefs: EmployerPostingPreferences,
): Omit<EmployerPostingPreferences, 'notify_on_new_applications'> {
  return {
    kind: prefs.kind,
    default_employment_type: prefs.default_employment_type,
    default_work_mode: prefs.default_work_mode,
    default_city: prefs.default_city,
    default_country_code: prefs.default_country_code,
    default_salary_visibility: prefs.default_salary_visibility,
  }
}

export function employerPostingFieldsEqual(
  a: EmployerPostingPreferences,
  b: EmployerPostingPreferences,
): boolean {
  return (
    a.default_employment_type === b.default_employment_type &&
    a.default_work_mode === b.default_work_mode &&
    a.default_city === b.default_city &&
    a.default_country_code === b.default_country_code &&
    a.default_salary_visibility === b.default_salary_visibility
  )
}

export function employerPostingPreferencesEqual(
  a: EmployerPostingPreferences,
  b: EmployerPostingPreferences,
): boolean {
  return (
    a.default_employment_type === b.default_employment_type &&
    a.default_work_mode === b.default_work_mode &&
    a.default_city === b.default_city &&
    a.default_country_code === b.default_country_code &&
    a.default_salary_visibility === b.default_salary_visibility &&
    a.notify_on_new_applications === b.notify_on_new_applications
  )
}
